import React, { useState } from "react";
import "../../../ProductDetails/childComponents/styles/OfferModal.css";
import "./styles/AutoShareContact.css";

import { useKeyboardOffset } from "../../../../../../hooks/useKeyboardOffset";
import { buildWhatsAppUrl } from "../../../../../../services/helpers/ContactHelper";

const WhatsAppIcon: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
    <path d="M12 0C5.373 0 0 5.373 0 12c0 2.125.557 4.126 1.533 5.859L0 24l6.335-1.512A11.95 11.95 0 0 0 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.818a9.812 9.812 0 0 1-5.003-1.371l-.36-.214-3.727.977.997-3.64-.234-.374A9.818 9.818 0 0 1 2.182 12C2.182 6.57 6.57 2.182 12 2.182S21.818 6.57 21.818 12 17.43 21.818 12 21.818z" />
  </svg>
);

const EyeIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const CopyIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const CheckIcon: React.FC = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

interface AutoShareContactProps {
  isOpen: boolean;
  onClose: () => void;
  phone: string;
  productTitle?: string;
}

const AutoShareContact: React.FC<AutoShareContactProps> = ({
  isOpen,
  onClose,
  phone,
  productTitle,
}) => {
  const [isRevealed, setIsRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const keyboardOffset = useKeyboardOffset();

  React.useEffect(() => {
    if (!isOpen) {
      setIsRevealed(false);
      setCopied(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const hasPhone = !!phone?.trim();
  const resolvedPhone = hasPhone ? phone.trim() : "N/A";
  const waUrl = buildWhatsAppUrl(resolvedPhone, productTitle);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(resolvedPhone);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.createElement("input");
      el.value = resolvedPhone;
      document.body.appendChild(el);
      el.select();
      document.execCommand("copy");
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div
      className="nf-offer-overlay"
      onClick={onClose}
      style={{ paddingBottom: keyboardOffset }}
    >
      <div
        className="nf-offer-card"
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} className="header-close-button" aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <div className="asc-header">
          <h3 className="asc-title">Contact is shared by seller</h3>
          <p className="asc-subtitle">
            Connect with the seller directly — you dont have to negotiate first.
          </p>
        </div>

        <a
          className={`asc-wa-btn${!hasPhone ? " asc-disabled" : ""}`}
          href={waUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={hasPhone ? undefined : (e) => e.preventDefault()}
        >
          <WhatsAppIcon size={20} />
          Connect on WhatsApp
        </a>

        <div className="asc-divider">or</div>

        <div className="asc-reveal-section">
          {!isRevealed ? (
            <button
              className="asc-reveal-btn"
              onClick={() => setIsRevealed(true)}
            >
              <EyeIcon />
              Reveal Contact Number
            </button>
          ) : (
            <div className="asc-phone-row">
              <span className="asc-phone-number">{resolvedPhone}</span>
              {hasPhone && (
                <button
                  className={`asc-copy-btn ${copied ? "copied" : ""}`}
                  onClick={handleCopy}
                  aria-label="Copy phone number"
                >
                  {copied ? <CheckIcon /> : <CopyIcon />}
                  {copied ? "Copied!" : "Copy"}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="nf-btn-row">
          <button className="nf-btn-cancel" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AutoShareContact;
